import * as UTIF from "utif";
import { normalizeSpecsImageUploadFile } from "../specsImageUpload";
import type { ImportImageInput } from "./types";

type CompositeImage = {
  dataUrl: string;
  width: number;
  height: number;
};

function toPngDataUrl(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): string {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to read image");
  }

  const imageData = context.createImageData(width, height);
  imageData.data.set(rgba);
  context.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

function readImageFile(file: File): Promise<CompositeImage> {
  return new Promise<CompositeImage>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error(`Failed to read "${file.name}"`));
        return;
      }

      const dataUrl = reader.result;
      const image = new Image();
      image.onload = () =>
        resolve({
          dataUrl,
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      image.onerror = () =>
        reject(
          new Error(
            `Could not decode "${file.name}" as a browser-supported image`
          )
        );
      image.src = dataUrl;
    };
    reader.onerror = () => reject(new Error(`Failed to read "${file.name}"`));
    reader.readAsDataURL(file);
  });
}

async function readTiffComposite(file: File): Promise<CompositeImage> {
  const buffer = await file.arrayBuffer();
  const ifds = UTIF.decode(buffer);
  const firstPage = ifds[0];
  if (!firstPage) {
    throw new Error("No decodable TIFF pages");
  }

  UTIF.decodeImage(buffer, firstPage);
  const { width, height } = firstPage;
  if (!width || !height) {
    throw new Error("No decodable TIFF pages");
  }

  return {
    dataUrl: toPngDataUrl(
      new Uint8ClampedArray(UTIF.toRGBA8(firstPage)),
      width,
      height
    ),
    width,
    height,
  };
}

function createInput(
  fileName: string,
  composite: CompositeImage
): ImportImageInput {
  return {
    fileName,
    width: composite.width,
    height: composite.height,
    compositeDataUrl: composite.dataUrl,
  };
}

export async function importImageFiles(
  files: File[]
): Promise<ImportImageInput> {
  if (files.length === 0) {
    throw new Error("Select an image to import");
  }

  const firstFile = files[0];
  const lowerName = firstFile.name.toLowerCase();
  if (lowerName.endsWith(".tiff") || lowerName.endsWith(".tif")) {
    return createInput(firstFile.name, await readTiffComposite(firstFile));
  }

  const normalized = await normalizeSpecsImageUploadFile(firstFile);
  return createInput(firstFile.name, await readImageFile(normalized.file));
}
